import InfoIcon from "@mui/icons-material/InfoOutlined";
import { Dialog, DialogContent, DialogTitle, Grid, IconButton, Typography } from "@mui/material";
import { GoogleMap, Marker } from "@react-google-maps/api";
import type { FC, PropsWithChildren } from "react";
import { useState } from "react";
import ProviderTermsAndConditionsContent from "../../admin/providers/ProviderTermsAndConditionsContent";
import { useGooglePlaceDetails } from "../../misc/googleMaps";
import ImagesGallery from "../imagesGallery/ImagesGallery";

const PlaceLocationMap = ({ googlePlaceId }: { googlePlaceId: string }) => {
  const { place } = useGooglePlaceDetails(googlePlaceId);
  if (!place) {
    return null;
  }
  return (
    <GoogleMap
      center={place.location || { lat: 0, lng: 0 }}
      zoom={place.location ? 15 : 2}
      mapContainerStyle={{ minWidth: "500px", width: "100%", height: "400px" }}
    >
      {place.location && <Marker position={place.location} />}
    </GoogleMap>
  );
};

export interface Product {
  readonly name: string | undefined;
  readonly description?: { readonly polish?: string };
  readonly contact?: {
    readonly address?: string;
    readonly googlePlaceId?: string;
  };
  readonly images: {
    readonly id: string;
    readonly name: string;
    readonly thumbnail: {
      readonly url: string;
    };
  }[];
}

interface Props {
  readonly product: Product | undefined;
  readonly providerId?: string;
}

type ProductDetailsDialogContentProps = PropsWithChildren<{
  readonly product: Product;
}>;

export const ProductDetailsDialogContent: FC<ProductDetailsDialogContentProps> = ({ product, children }) => (
  <Grid spacing={2} container>
    <Grid size={12}>
      <Typography variant="subtitle2">Nazwa</Typography>
      <Typography>{product.name ?? "n.d."}</Typography>
    </Grid>
    <Grid size={12}>
      <Typography variant="subtitle2">Opis</Typography>
      <Typography>{product.description?.polish ?? "n.d."}</Typography>
    </Grid>
    <Grid size={12}>
      <Typography variant="subtitle2">Adres</Typography>
      <Typography>{product.contact?.address ?? "n.d."}</Typography>
    </Grid>
    {product.contact?.googlePlaceId && (
      <Grid size={12}>
        <PlaceLocationMap googlePlaceId={product.contact.googlePlaceId} />
      </Grid>
    )}
    {product.images.length > 0 && (
      <Grid size={12}>
        <ImagesGallery images={product.images.map((i) => ({ name: i.name, url: i.thumbnail.url }))} maxWidth={500} />
      </Grid>
    )}
    {children}
  </Grid>
);

const ProductDetails: FC<Props> = ({ product, providerId }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <>
      <IconButton size="small" aria-label="Szczegóły produktu" onClick={() => setDialogOpen(true)}>
        <InfoIcon fontSize="inherit" />
      </IconButton>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth>
        <DialogTitle>Szczegóły produktu</DialogTitle>
        <DialogContent data-testid="product-details-dialog-content">
          {product ? (
            <ProductDetailsDialogContent product={product}>
              {providerId && <ProviderTermsAndConditionsContent providerId={providerId} enabled={dialogOpen} />}
            </ProductDetailsDialogContent>
          ) : (
            <Typography>szczegóły niedostępne</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductDetails;
